import React, {useEffect, useState} from 'react';
import {Breadcrumb, Col, ColorPicker, Descriptions, Image, Popconfirm, Row, Space, Table, Tabs, Tag} from 'antd';
import {BarsOutlined} from "@ant-design/icons";
import {BreadcrumbItemType, BreadcrumbSeparatorType} from "antd/es/breadcrumb/Breadcrumb";
import {useLocation, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import { useTranslation } from 'react-i18next';

// Types
import {AllianceStockType} from "../../types/AllianceStockTypes";
import { ModelType } from '../../types/ProductTypes';
import { VariantType } from '../../types/VariantTypes';

// Store
import {getDetail, activateCatalog} from "../../store/redux/actions/catalogActions";
import {useAppDispatch} from "../../store/store";
import * as catalogSelectors from "../../store/redux/selectors/catalogSelectors";

// Utils
import {routes} from "../../utils/routes";
import {currencyFormat, redirectWindow} from "../../utils/generalUtils";
import tableColumns from "../../utils/tableColumns";

// Components
import StockFormModal from "./StockFormModal";
import ButtonComponent from '../subComponents/ButtonComponent';


const ProductDetail = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const breadcrumbItems: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] = [
    { href: routes(t).catalog.root, title: <><BarsOutlined /><span>{t('catalog')}</span></> },
    { title: t('productDetail') },
  ];
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [stockToEdit, setStockToEdit] = useState<AllianceStockType | undefined>(undefined);
  const productDetail = useSelector(catalogSelectors.productDetailSelector)!;
  const {supermodel} = useParams();
  const {search} = useLocation();
  const [selectedModelIndex, setSelectedModelIndex] = useState<number>(0);
  const [selectedModel, setSelectedModel] = useState<ModelType>();
  const renderComponent = productDetail.supermodel !== undefined;

  useEffect(() => {
    supermodel ? dispatch(getDetail(supermodel, new URLSearchParams(search))) : redirectWindow(routes(t).catalog.root);
  }, [dispatch, search, supermodel, t]);

  useEffect(() => {
    if (productDetail.modelVariants && productDetail.modelVariants.length > 0) {
      setSelectedModel(productDetail.modelVariants[selectedModelIndex]);
    }
  },[productDetail, selectedModelIndex]);

  const handleSelectModel = (modelIndex: number) => {
    setSelectedModelIndex(modelIndex);
    setSelectedModel(productDetail.modelVariants[modelIndex]);
  }

  const handleEditStock = (isVisible: boolean, stock?: AllianceStockType) => {
    setStockModalVisible(isVisible);
    setStockToEdit(stock);
  }

  const handleDeactivateCatalog = () => {
    dispatch(activateCatalog({supermodel: productDetail.supermodel ?? '', isActive: false}, 'detail'));
  }

  const handleActivateCatalog = () => {
    dispatch(activateCatalog({supermodel: productDetail.supermodel ?? '', isActive: true}, 'detail'));
  }

  const itemVariants = (variants: VariantType[]) => {
    return variants.map(variant => ({
      key: variant.code,
      label: variant.code + ' - ' + variant.size,
      children: 
        <Table rowKey={'code'} key={variant.code} size={'small'} bordered={true}
          scroll={{ x:  700 }}
          caption={
            <Descriptions key={variant.code} bordered={true} size={'small'}
                          column={{ xxl: 4, xl: 3, lg: 3, md: 1, sm: 1, xs: 1 }}>
              <Descriptions.Item label={t('code')}>{variant.code}</Descriptions.Item>
              <Descriptions.Item label={t('size')}>{variant.size}</Descriptions.Item>
              <Descriptions.Item label={t('color')}>
                <Space>
                  <ColorPicker format={'hex'} value={variant.color} disabled={true} />
                  <span>HEX: <span>{variant.color}</span></span>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('dimensions')}>{variant.dimensions}</Descriptions.Item>
              <Descriptions.Item label={t('weight')}>{variant.weight}</Descriptions.Item>
              <Descriptions.Item label={t('price')}>{t('currencySymbol')} {currencyFormat(variant.price?.toString())}</Descriptions.Item>
            </Descriptions>
          }
          dataSource={variant.stocks}
          columns={tableColumns(
            'variants',
            (stock: AllianceStockType) => handleEditStock(true, stock)
          )} />
    }));
  }

  return (
    <Row>
      {renderComponent &&
        <>
          <Col md={24} xs={24} className={'px-5'}>
            <Breadcrumb items={breadcrumbItems} />
          </Col>

          <Col md={10} xs={24} className={'min-h-96 flex p-5 [&>div]:w-full [&>div]:m-auto static'}>
            <Image src={selectedModel?.imageUrl} />
          </Col>
          <Col md={14} xs={24} className={'min-h-96 flex p-5'}>
            <Space direction={"vertical"} size={'middle'}>
              <Col md={24} xs={24} className={'text-lg -mb-2 text-blue-dkt-500'}>
                {productDetail.brand}
              </Col>
              <Col md={24} xs={24} className={'font-bold text-lg -mt-2'}>
                {selectedModel?.name}
              </Col>
              <Col md={24} xs={24} className={'font-semibold -mt-2'}>
                <span>{t('model')}:</span> {selectedModel?.model}
              </Col>
              <Col>
                {productDetail.alliances?.map(alliance =>
                  <Tag style={{ backgroundColor: alliance.color }} className={`border-none text-white my-1`}
                       key={alliance.allianceId} closable={false}>
                    {alliance.name}
                  </Tag>
                )}
              </Col>
              <Col>
                {productDetail.practices?.map((practice, index) =>
                  <Tag className={'bg-blue-dkt-300 border-blue-dkt-400 text-white my-1'}
                       key={index} closable={false}>
                    {practice}
                  </Tag>
                )}
              </Col>
              <Col md={24} xs={24} className={'text-md'}>
                {selectedModel?.description}
              </Col>
              <Col md={24} xs={24} className={'mt-5'}>
                {productDetail.isActive !== false ?
                  <Popconfirm title={t('removeCatalogModelConfirm')}
                              okText={t('confirm')}
                              cancelText={t('cancel')}
                              onConfirm={() => handleDeactivateCatalog()}>
                    <ButtonComponent key={'submit'}
                                    text={t('removePost')}
                                  className={'h-8 text-white bg-blue-dkt-400 hover:bg-blue-dkt-500 focus:outline-none ' +
                                    'focus:ring-2 focus:ring-offset-2 focus:ring-blue-dkt-300 after:content-none'}
                                  />
                  </Popconfirm>
                :
                  <Popconfirm title={t('includeCatalogModelConfirm')}
                              okText={t('confirm')}
                              cancelText={t('cancel')}
                              onConfirm={() => handleActivateCatalog()}>
                    <ButtonComponent key={'submit'}
                                    text={t('includePost')}
                                  className={'h-8 text-white bg-blue-dkt-400 hover:bg-blue-dkt-500 focus:outline-none ' +
                                    'focus:ring-2 focus:ring-offset-2 focus:ring-blue-dkt-300 after:content-none'}
                                  />
                  </Popconfirm>                    
                }
              </Col>
              <Col md={24} xs={24} className={'mt-5'}>
                {productDetail.imageUrls?.map((imageUrl, index) => (
                  <Col key={`${productDetail.model}-${index}`} className={'relative inline-block m-1'}>
                    <Space direction={"vertical"} size={1} >
                      <span className={'center -translate-x-1/2 text-sm px-1'}>
                        {productDetail.modelVariants[index]?.model ?? ''}
                      </span>
                      <Image src={imageUrl} width={80} height={80}
                        preview={false} onClick={() => handleSelectModel(index)}
                        className={'hover:cursor-pointer' + (selectedModelIndex === index ? ' border-2 border-blue-dkt-500' : '')}
                        title={ productDetail.modelVariants[index]?.model ?? '' }
                      />
                    </Space>
                  </Col>
                ))}
              </Col>
            </Space>
          </Col>

          <Col md={24} xs={24} className={'p-1'}>
            <h1 className={'text-lg font-semibold mb-5'}>{t('variants')}</h1>
            <Tabs type={'card'} size={'small'} items={itemVariants(selectedModel?.variants || [])} />
          </Col>
        </>
      }

      {stockModalVisible &&
        <StockFormModal stockToEdit={stockToEdit}
                        supermodel={productDetail.supermodel}
                        params={new URLSearchParams(search)}
                        onToggleModal={handleEditStock} />
      }
    </Row>
  );
}

export default ProductDetail;